'use client'
import { useEffect, useState, useCallback } from 'react'
import AnimationComponent from '../../global/interactive/AnimationComponent'
import { cx } from '@/src/styles/stylebook'
import type { MenuItem } from '@/src/types'
import Link from 'next/link'

interface SitemapProps {
  animationData: any
  isOpen: boolean
  onClose: () => void
  menuItems: MenuItem[]
}

const frames = {
  open: [0, 29] as [number, number],
  close: [60, 89] as [number, number],
}

const Sitemap = ({ animationData, isOpen, onClose, menuItems }: SitemapProps) => {
  const [isOpening, setIsOpening] = useState(false)
  const [isClosing, setIsClosing] = useState(false)
  const [currentFrames, setCurrentFrames] = useState<[number, number] | null>(null)
  const [shouldRender, setShouldRender] = useState(false)
  const [shouldPlay, setShouldPlay] = useState(false)

  // 열기 처리
  useEffect(() => {
    if (isOpen && !isOpening && !isClosing) {
      console.log('열기 시작: 0~29 프레임 재생')
      setIsOpening(true)
      setCurrentFrames(frames.open)
      setShouldRender(true)

      // AnimationComponent가 mount된 후 애니메이션 시작
      setTimeout(() => {
        setShouldPlay(true)
      }, 50)
    }
  }, [isOpen, isOpening, isClosing])

  // 닫기 처리 - Countdown 방식 참고
  const handleClose = useCallback(() => {
    if (isOpening && !isClosing) {
      console.log('닫기 시작: 60~89 프레임 재생')
      setIsClosing(true)
      setCurrentFrames(frames.close)
      setShouldPlay(true)
    }
  }, [isOpening, isClosing])

  // ESC 키로 닫기
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpening && !isClosing) {
        handleClose()
      }
    }

    if (isOpening) {
      document.addEventListener('keydown', handleKeyDown)
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpening, isClosing, handleClose])

  // 애니메이션 완료 처리
  const handleAnimationComplete = useCallback(() => {
    if (isClosing) {
      // 닫기 애니메이션 완료 → 숨김 및 부모에게 알림
      setIsClosing(false)
      setIsOpening(false)
      setShouldRender(false)
      setShouldPlay(false)
      onClose() // 부모 컴포넌트에 닫힘 알림
    } else if (isOpening && currentFrames === frames.open) {
      // 열기 애니메이션 완료 → 29프레임에서 정지하고 열린 상태 유지
      setShouldPlay(false)
      console.log('열기 애니메이션 완료: 29프레임에서 정지')
    }
  }, [isClosing, isOpening, currentFrames, onClose])

  // 배경 클릭 시 닫기
  const handleBackgroundClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      if (e.target === e.currentTarget) {
        handleClose()
      }
    },
    [handleClose]
  )

  if (!shouldRender) return null

  return (
    <div
      className={cx(
        'fixed top-0 left-0 z-300 h-full w-full cursor-pointer transition-all duration-300 ease-in-out select-none',

        // shouldRender가 true면 바로 배경 보이게 (애니메이션 시작과 동시에)
        shouldRender ? 'bg-gray-0/50 block opacity-100' : 'bg-transparent opacity-0'
      )}
      data-layer="Sitemap"
      onClick={handleBackgroundClick}
    >
      <AnimationComponent
        animationData={animationData}
        startFrame={currentFrames?.[0]}
        endFrame={currentFrames?.[1]}
        loop={false}
        autoplay={false}
        play={shouldPlay}
        onComplete={handleAnimationComplete}
        className="pointer-events-none absolute top-0 left-0 h-full w-full"
        preserveAspectRatio="none"
      />

      <div
        className={cx(
          'absolute left-1/2 max-h-[80vh] w-full max-w-[1920px] -translate-x-1/2 transform overflow-auto pt-6 transition-opacity duration-300 ease-in-out',
          // 열기 또는 닫기 애니메이션 중에는 보이게 유지
          isClosing || shouldPlay || !shouldRender ? 'opacity-0' : 'opacity-100'
        )}
        onClick={e => e.stopPropagation()}
      >
        {/* 닫기 버튼 */}
        <button
          onClick={handleClose}
          className="text-gray-10 h3 absolute top-4 right-4 leading-none transition-opacity hover:opacity-70"
        >
          ×
        </button>

        {/* 사이트맵 내용 */}
        <div className="px-marg">
          <div
            className={cx(
              'flex justify-start gap-x-21',
              'scroll-hide flex-nowrap max-md:max-h-[90vh] max-md:flex-col max-md:items-center max-md:gap-y-5 max-md:overflow-y-auto'
            )}
            data-lenis-prevent
          >
            {menuItems
              .filter(menuItem => menuItem.active)
              .map((menuItem, index) => {
                if (!menuItem.slug?.current) return null

                // 첫 번째 활성 서브메뉴 찾기 (타입 가드 포함)
                const firstActiveSubMenu = menuItem.subMenu?.find(
                  sub =>
                    sub &&
                    typeof sub === 'object' &&
                    'active' in sub &&
                    sub.active &&
                    'slug' in sub &&
                    sub.slug?.current
                )

                const mainHref =
                  firstActiveSubMenu && 'slug' in firstActiveSubMenu
                    ? `/${menuItem.slug.current}/${firstActiveSubMenu.slug?.current || ''}`
                    : `/${menuItem.slug.current}`

                return (
                  <div key={`sitemap-${menuItem.slug.current}-${index}`}>
                    <h3 className="h6">
                      <Link href={mainHref} className="text-gray-60 hover:underline">
                        {menuItem.title}
                      </Link>
                    </h3>

                    {menuItem.subMenu && menuItem.subMenu.length > 0 && (
                      <ul className="ml-0">
                        {menuItem.subMenu
                          .filter(
                            subItem =>
                              subItem &&
                              typeof subItem === 'object' &&
                              'active' in subItem &&
                              subItem.active &&
                              'slug' in subItem &&
                              subItem.slug?.current
                          )
                          .map((subItem, subIndex) => {
                            // 타입 체크를 통과한 subItem은 안전하게 사용 가능
                            if (!('slug' in subItem) || !subItem.slug?.current) return null

                            return (
                              <li key={`sitemap-sub-${subItem.slug.current}-${subIndex}`}>
                                <Link
                                  href={`/${menuItem.slug?.current}/${subItem.slug.current}`}
                                  className="h6 text-gray-10 hover:underline"
                                >
                                  {'title' in subItem ? subItem.title : ''}
                                </Link>
                              </li>
                            )
                          })}
                      </ul>
                    )}
                  </div>
                )
              })}
          </div>
        </div>
      </div>
    </div>
  )
}

// SitemapTrigger 컴포넌트 - 버튼과 Sitemap을 함께 관리
export const SitemapTrigger = ({
  animationData,
  menuItems,
}: {
  animationData: any
  menuItems: MenuItem[]
}) => {
  const [isSitemapOpen, setIsSitemapOpen] = useState(false)

  return (
    <>
      <button
        className="cursor-pointer hover:underline max-md:mb-4 md:max-lg:order-4"
        onClick={() => setIsSitemapOpen(true)}
      >
        사이트맵
      </button>
      <Sitemap
        animationData={animationData}
        isOpen={isSitemapOpen}
        onClose={() => setIsSitemapOpen(false)}
        menuItems={menuItems}
      />
    </>
  )
}

export default Sitemap
